import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import { useToasts } from 'common/toast';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { Dropdown, DropdownProps, Icon, Message, Pagination, SemanticCOLORS, SemanticICONS, Select } from 'semantic-ui-react';
import ImpersonationPickerModal from './ImpersonationPickerModal';
import { HeaderCell,  Table,  TableBody,  TableCell,  TableHeader,  TableRow } from 'common/components/Table';
import { PageContent, PageHeader, PageSection } from 'common/styles/page';
import { StyledAddButton } from 'common/components/button/StyledButton';

import { AppDispatch } from '../../../../store/store';
import { AccountRead } from '../../../../store/generated/admin/account';
import {
  isCurrentUserStaffOrSuperAdmin,
  isCurrentUserSuperUser,
  getCurrentAccount,
  getCurrentOrganization,
  getOrganizations,
  ACCOUNT_ROLE,
} from '../../../../common/selectors/auth';
import { beginImpersonation } from '../../../../common/utils/impersonationFlow';
import { useTrackingCode } from 'react-hubspot-tracking-code-hook';
import { accountApi } from '../api/accountApi';


import AddAccountModal from './AddAccountModal';

const AccountsManagement: React.FC = () => {
  const { addToast } = useToasts();
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const isAdmin = useSelector(isCurrentUserStaffOrSuperAdmin);
  const isSuperUser = useSelector(isCurrentUserSuperUser);
  const currentAccount = useSelector(getCurrentAccount);
  const currentOrganization = useSelector(getCurrentOrganization);
  const organizations = useSelector(getOrganizations);
  const canManageAccounts = useMemo(() => isAdmin, [isAdmin]);
  const [organizationFilter, setOrganizationFilter] = useState<number>(0);
  const [isAddEditModalOpen, setIsAddEditModalOpen] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState<AccountRead | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [isImpersonationModalOpen, setIsImpersonationModalOpen] = useState(false);
  const pickMode: 'impersonate' | null =
    searchParams.get('action') === 'impersonate' && isAdmin ? 'impersonate' : null;

  // True when an account is a valid impersonation target. Mirrors the
  // server-side rules in admin_api/serializers/impersonation.py.
  const canImpersonate = useCallback(
    (account: AccountRead): boolean =>
      account.id !== currentAccount?.id
      && account.role !== ACCOUNT_ROLE.SUPERUSER
      && account.role !== ACCOUNT_ROLE.STAFF
      && Boolean(account.is_active),
    [currentAccount?.id],
  );

  const {
    data: accountsData,
    isLoading: isLoadingAccounts,
    error: accountsError,
  } = accountApi.useApiAdminV1AccountsListQuery(
    {
      id: 0,
      ...(organizationFilter > 0 ? { organization_id: organizationFilter } : {}),
    },
    { skip: !canManageAccounts }
  );

  function getRoleText(role: number | undefined) {
    switch (role) {
      case 1:
        return 'Admin';
      case 2:
        return 'Staff';
      case 3:
        return 'Regular User';
      default:
        return 'Unknown Role';
    }
  }

  function getAccountTypeIcon(account: any) {
    let roleIcon = 'user' as SemanticICONS;
    let roleColor = 'grey' as SemanticCOLORS;
    let roleTitle = getRoleText(account.role);

    if (account.role === 1) {
      roleIcon = 'shield' as SemanticICONS; // Organization Admin
      roleColor = 'green' as SemanticCOLORS;
    } else if (account.role === 2) {
      roleIcon = 'users' as SemanticICONS; // Team Leader
      roleColor = 'blue' as SemanticCOLORS;
    }
    return { roleIcon, roleColor, roleTitle };
  }

  const [currentPage, setCurrentPage] = useState(1);
  const PAGE_SIZE = 9;
  const [statusFilter, setStatusFilter] = useState<string>('active');
  const [typeFilter, setTypeFilter] = useState<number>(0);
  const [nameFilter, setNameFilter] = useState('');

  const handleStatusFilterChange = useCallback(
    (event: React.SyntheticEvent, data: DropdownProps) => {
      setStatusFilter(data.value as string);
      setCurrentPage(1);
    },
    []
  );

  const handleTypeFilterChange = useCallback((event: React.SyntheticEvent, data: DropdownProps) => {
    const value = typeof data.value === 'string' ? parseInt(data.value, 10) : Number(data.value);
    setTypeFilter(!isNaN(value) ? value : 0);
    setCurrentPage(1);
  }, []);

  const [sortConfig, setSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' }>({
    key: 'last_name',
    direction: 'asc',
  });

  const handleSort = (key: string) => {
    const direction = sortConfig.key === key && sortConfig.direction === 'asc' ? 'desc' : 'asc';
    setSortConfig({ key, direction });
    setCurrentPage(1);
  };

  const filteredAccounts = useMemo(() => {
    if (!accountsData) return [];

    const lowerName = nameFilter.trim().toLowerCase();

    const baseAccounts = organizationFilter === -1
      ? accountsData.filter(account => account.role === ACCOUNT_ROLE.SUPERUSER)
      : organizationFilter === -2
        ? accountsData.filter(account => account.role === ACCOUNT_ROLE.STAFF)
        : accountsData;

    const filtered = baseAccounts.filter((account) => {
      const matchesStatus =
        statusFilter === 'active'
          ? account.is_active
          : statusFilter === 'inactive'
            ? !account.is_active
            : true;

      const matchesType = typeFilter === 0 ? true : account.role === typeFilter;

      const matchesName =
        !lowerName ||
        account.first_name?.toLowerCase().startsWith(lowerName) ||
        account.last_name?.toLowerCase().startsWith(lowerName);

      return matchesStatus && matchesType && matchesName;
    });

    const getRoleLabel = (r?: number) => getRoleText(r).toLowerCase();

    return [...filtered].sort((a, b) => {
      let aVal: any;
      let bVal: any;

      if (sortConfig.key === 'is_active') {
        aVal = a.is_active ? 1 : 0;
        bVal = b.is_active ? 1 : 0;
      } else if (sortConfig.key === 'last_name') {
        aVal = a.last_name ?? '';
        bVal = b.last_name ?? '';
      } else if (sortConfig.key === 'email') {
        aVal = a.email ?? '';
        bVal = b.email ?? '';
      } else if (sortConfig.key === 'role') {          // ← add this
    aVal = getRoleLabel(a.role);
    bVal = getRoleLabel(b.role);
  }
   else {
        aVal = '';
        bVal = '';
      }

      if (typeof aVal === 'string') aVal = aVal.toLowerCase();
      if (typeof bVal === 'string') bVal = bVal.toLowerCase();

      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [accountsData, organizationFilter, statusFilter, typeFilter, nameFilter, sortConfig]);

  const paginatedAccounts = useMemo(() => {
    const startIndex = (currentPage - 1) * PAGE_SIZE;
    return filteredAccounts.slice(startIndex, startIndex + PAGE_SIZE);
  }, [filteredAccounts, currentPage]);

  const totalPages = useMemo(
    () => Math.ceil(filteredAccounts.length / PAGE_SIZE),
    [filteredAccounts]
  );

  const [addAccount, { isLoading: isAddingAccount }] =
    accountApi.useApiAdminV1AccountsCreateMutation();

  const [updateAccount, { isLoading: isUpdating }] =
    accountApi.useApiAdminV1AccountsPartialUpdateMutation();

  const [formError, setFormError] = useState('');
  const [isTogglingStatus, setIsTogglingStatus] = useState(false);
  const [openDropdownId, setOpenDropdownId] = useState<string | null>(null);

  // Combine loading states
  const isLoading = isLoadingAccounts || isAddingAccount || isTogglingStatus;
  const { setPathPageView } = useTrackingCode();

  useEffect(() => {
    setPathPageView('/settings/accounts');
  }, [setPathPageView]);

  useEffect(() => {
    if (pickMode === 'impersonate') {
      setIsImpersonationModalOpen(true);
    }
  }, [pickMode]);

  const handleCloseImpersonationModal = () => {
    setIsImpersonationModalOpen(false);
    if (pickMode === 'impersonate') {
      searchParams.delete('action');
      setSearchParams(searchParams, { replace: true });
    }
  };

  const handleSaveAccount = async (account: AccountRead) => {
    setFormError('');

    if (!account.email || !account.first_name || !account.last_name || !account.role) {
      setFormError('Please fill in all required fields');
      return;
    }

    try {
      if (account.id) {
        // Edit existing account
        await updateAccount({
          id: account.id,
          pk: account.id,
          patchedAccountCreateUpdate: {
            email: account.email,
            first_name: account.first_name,
            last_name: account.last_name,
            account_type: account.role as 4 | 3 | 2 | 1,
          },
        }).unwrap();

        addToast('Account updated successfully', { appearance: 'success', autoDismiss: true });
      } else {
        // Add new account
        await addAccount({
          id: 0, // Temporary ID for create
          accountCreateUpdate: {
            email: account.email,
            first_name: account.first_name,
            last_name: account.last_name,
            account_type: account.role as 4 | 3 | 2 | 1,
          },
        }).unwrap();

        addToast('Account added successfully', { appearance: 'success', autoDismiss: true });
      }

      setIsAddEditModalOpen(false);
      setSelectedAccount(null);
    } catch (error) {
      console.error('Failed to save account:', error);
      setFormError('Failed to save account. Please try again.');

      addToast('Failed to save account', {
        appearance: 'error',
        autoDismiss: true,
      });
      setIsAddEditModalOpen(false);
    }
  };

  // Use the toggle status mutation hook
  const [toggleAccountStatus, { isLoading: isToggling }] =
    accountApi.useApiAdminV1AccountsToggleStatusCreateMutation();

  // Handle toggling account status
  const handleToggleStatus = async (account: AccountRead) => {
    if (!canManageAccounts) {
      addToast('You do not have permission to update account status', { appearance: 'error' });
      return;
    }

    // Prevent a user from archiving their own account
    if (account.id === currentAccount?.id && account.is_active) {
      addToast('You cannot archive your own account', { appearance: 'error' });
      return;
    }

    setIsTogglingStatus(true);

    try {
      // Call the actual API endpoint to toggle the account status
      // @ts-ignore
      await toggleAccountStatus({ id: account.id.toString() }).unwrap();

      // Get the current status for the toast message
      const currentStatus = account.is_active || false;
      const newStatus = !currentStatus;

      addToast(`Account ${newStatus ? 'activated' : 'deactivated'}`, {
        appearance: 'success',
        autoDismiss: true,
      });

      // The query will automatically refetch due to tag invalidation
    } catch (error) {
      console.error('Failed to toggle account status:', error);

      addToast('Failed to update account status', {
        appearance: 'error',
        autoDismiss: true,
      });
    } finally {
      setIsTogglingStatus(false);
    }
  };

  if (!canManageAccounts) {
    return (
      <PageSection>
        <Message negative>
          <Message.Header>Access Denied</Message.Header>
          <p>You do not have permission to manage accounts.</p>
        </Message>
      </PageSection>
    );
  }

  return (
    <PageContent data-testid="admin-accounts-page">
      {pickMode === 'impersonate' && (
        <Message info data-testid="impersonate-pick-banner">
          <Message.Header>Pick a user to impersonate</Message.Header>
          <p>Click any active regular user below to begin. Super admins, staff, and archived accounts are not eligible.</p>
        </Message>
      )}
      <PageHeader
        data-testid="admin-accounts-header"
        title={`Account Management`}
        divider
        controls={
          <>
            <StyledAddButton
              data-testid="admin-accounts-add"
              onClick={() => setIsAddEditModalOpen(true)}
              label="Add Account"
              width="130px"
              height="34px"
            />
          </>
        }
      />

      <PageSection
        data-testid="admin-accounts-content"
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        padbottom
      >
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          <Select
            fluid
            search
            options={
              [{ key: 'all', text: 'All Accounts', value: '0' },
                { key: 'staff', text: '[Staff]', value: '-2' },
                { key: 'admin', text: '[Admin]', value: '-1' },
              ]
                .concat(
                  (organizations ?? []).map((org) => ({
                    key: String(org.id),
                    text: org.name,
                    value: String(org.id),
                  }))
                )
            }
            value={String(organizationFilter)}
            onChange={(_, data) => {
              setOrganizationFilter(Number(data.value));
              setCurrentPage(1);
            }}
            style={{
              minWidth: '220px',
              height: '38px',
              border: '1px solid #ccc',
              borderRadius: '5px',
              fontSize: '14px',
            }}
          />
          <input
            type="text"
            placeholder="Filter by name"
            value={nameFilter}
            onChange={e => {
              setNameFilter(e.target.value);
              setCurrentPage(1);
            }}
            style={{
              height: '38px',
              border: '1px solid #ccc',
              borderRadius: '5px',
              padding: '0 10px',
              fontSize: '14px',
              minWidth: '300px',
            }}
          />
        </div>

        <div style={{ display: 'flex', gap: '10px' }}>
          <Dropdown
            placeholder="Filter by status"
            selection
            value={statusFilter}
            compact
            onChange={handleStatusFilterChange}
            options={[
              { key: 'active', text: 'Active', value: 'active' },
              { key: 'inactive', text: 'Archived', value: 'inactive' },
            ]}
            style={{
              height: '36px',
              border: '1px solid #ccc',
              borderRadius: '5px',
              fontSize: '14px',
              minWidth: '120px',
            }}
          />
        </div>
      </PageSection>

      <PageSection>
        {isLoadingAccounts ? (
          <EmptyState>Loading accounts...</EmptyState>
        ) : accountsError ? (
          <EmptyState>Error loading accounts. Please try again.</EmptyState>
        ) : !accountsData || accountsData.length === 0 ? (
          <EmptyState>No accounts found</EmptyState>
        ) : (
          <Table data-testid="admin-accounts-table">
            <TableHeader>
              <TableRow>
                <HeaderCell selectable onClick={() => handleSort('last_name')} width="4">
                  Name{' '}
                  {sortConfig.key === 'last_name' && (
                    <Icon name={sortConfig.direction === 'asc' ? 'caret up' : 'caret down'} />
                  )}
                </HeaderCell>
                <HeaderCell selectable onClick={() => handleSort('email')} width="5">
                  Email{' '}
                  {sortConfig.key === 'email' && (
                    <Icon name={sortConfig.direction === 'asc' ? 'caret up' : 'caret down'} />
                  )}
                </HeaderCell>
                <HeaderCell selectable onClick={() => handleSort('role')} width="3">
                  Role{' '}
                  {sortConfig.key === 'role' && (
                    <Icon name={sortConfig.direction === 'asc' ? 'caret up' : 'caret down'} />
                  )}
                </HeaderCell>
                <HeaderCell selectable onClick={() => handleSort('is_active')} width="3">
                  Status{' '}
                  {sortConfig.key === 'is_active' && (
                    <Icon name={sortConfig.direction === 'asc' ? 'caret up' : 'caret down'} />
                  )}
                </HeaderCell>
                <HeaderCell width="1" justify="center">
                  Actions
                </HeaderCell>
              </TableRow>
            </TableHeader>

            <TableBody>
              {paginatedAccounts.map((account: any) => {
                let { roleIcon, roleColor, roleTitle } = getAccountTypeIcon(account);
                return (
                  <TableRow
                    key={account.id}
                    $selectable
                    style={
                      pickMode === 'impersonate' && !canImpersonate(account)
                        ? { opacity: 0.4 }
                        : undefined
                    }
                    onClick={async () => {
                      if (pickMode === 'impersonate') {
                        if (!canImpersonate(account)) return;
                        try {
                          await beginImpersonation(dispatch, navigate, account.id, '');
                        } catch (err) {
                          addToast(
                            err instanceof Error ? err.message : 'Failed to start impersonation.',
                            { appearance: 'error', autoDismiss: true },
                          );
                        }
                        return;
                      }
                      setSelectedAccount(account);
                      setIsAddEditModalOpen(true);
                    }}
                  >
                    <TableCell $firstcol>
                      <Icon name={roleIcon} color={roleColor} title={roleTitle} />{' '}
                      {account.last_name}, {account.first_name}
                    </TableCell>
                    <TableCell>{account.email}</TableCell>
                    <TableCell>{getRoleText(account.role)}</TableCell>
                    <TableCell>
                      <ValidationIcon>
                        <Icon
                          name={account.is_active ? 'check circle' : 'archive'}
                          color={account.is_active ? 'green' : 'red'}
                          title={account.is_active ? 'Active' : 'Archived'}
                          size="large"
                        />
                        <span>{account.is_active ? 'Active' : 'Archived'}</span>
                      </ValidationIcon>
                    </TableCell>
                    <TableCell justify="center">
                      <Dropdown
                        button
                        icon="ellipsis vertical"
                        floating
                        className="icon"
                        direction="left"
                        style={{ width: '38px', height: '35px', borderRadius: '8px' }}
                        open={openDropdownId === account.id}
                        onClick={e => {
                          e.stopPropagation();
                          setOpenDropdownId(openDropdownId === account.id ? null : account.id);
                        }}
                        onBlur={() => setOpenDropdownId(null)}
                      >
                        <Dropdown.Menu>
                          <Dropdown.Item
                            text="Edit Account"
                            onClick={e => {
                              e.stopPropagation();
                              setSelectedAccount(account);
                              setIsAddEditModalOpen(true);
                            }}
                          />
                          <Dropdown.Item
                            text={!account.is_active ? 'Activate Account' : 'Archive Account'}
                            onClick={e => {
                              e.stopPropagation();
                              setOpenDropdownId(null);
                              // Prevent a user from archiving their own account
                              if (account.id === currentAccount?.id && account.is_active) {
                                addToast('You cannot archive your own account', { appearance: 'error' });
                                return;
                              }
                              handleToggleStatus(account);
                            }}
                          />
                          {isSuperUser
                            && account.id !== currentAccount?.id
                            && account.role !== ACCOUNT_ROLE.SUPERUSER
                            && account.role !== ACCOUNT_ROLE.STAFF
                            && account.is_active && (
                              <Dropdown.Item
                                text="Impersonate"
                                onClick={async e => {
                                  e.stopPropagation();
                                  setOpenDropdownId(null);
                                  try {
                                    await beginImpersonation(dispatch, navigate, account.id, '');
                                  } catch (err) {
                                    addToast(
                                      err instanceof Error
                                        ? err.message
                                        : 'Failed to start impersonation.',
                                      { appearance: 'error', autoDismiss: true },
                                    );
                                  }
                                }}
                              />
                            )}
                        </Dropdown.Menu>
                      </Dropdown>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </PageSection>

      <PageSection display="flex" justifyContent="end" padtop>
        <Pagination
          activePage={currentPage}
          totalPages={totalPages}
          onPageChange={(_, data) => setCurrentPage(data.activePage as number)}
          firstItem={null}
          lastItem={null}
          prevItem={{ content: '‹', icon: true }}
          nextItem={{ content: '›', icon: true }}
        />
      </PageSection>

      <AddAccountModal
        open={isAddEditModalOpen}
        formError={formError}
        onClose={() => setIsAddEditModalOpen(false)}
        onSave={handleSaveAccount}
        loading={isLoading}
        initialAccount={selectedAccount}
      />

      <ImpersonationPickerModal
        open={isImpersonationModalOpen}
        onClose={handleCloseImpersonationModal}
      />

    </PageContent>
  );
};

export default AccountsManagement;

const EmptyState = styled.div`
  padding: 20px;
  text-align: center;
  background-color: #f9f9f9;
  border-radius: 4px;
  color: #666;
`;

const ValidationIcon = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.2rem; /* Adjust spacing between icon and text */

  .ui.icon {
    margin: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }

  span {
    display: inline-flex;
    align-items: center;
  }
`;
